import React from "react";
import { Switch } from "@material-ui/core";
import { useParams } from "react-router-dom";
import { ErrorBox, Loading, Pane, Text, HFlex, Icon, useErrorContext } from "shared";
import { Circle } from "assets";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import {
  HealthCareOrganization,
  useHealthCareOrganization,
  useUpdateHealthCareOrganization,
} from "../queries/useHealthCareOrganizations";
import HcoInfo from "./HcoInfo";

export const Account: React.VFC = () => {
  const { t } = useTranslation();
  const { item } = useParams<{ item: string }>();
  const id = Number(item) || 0;
  const { data: hco, error, isLoading, isSuccess } = useHealthCareOrganization(id);
  const queryClient = useQueryClient();
  const setErrors = useErrorContext();
  const { mutateAsync } = useUpdateHealthCareOrganization();

  const handleStatusToggle = async () => {
    if (!hco) return;
    const hcoKey = queryKeys.healthCareOrganization(id);
    const rollbackData = queryClient.getQueryData(hcoKey);
    const newStatus = hco.status === "active" ? "inactive" : "active";
    queryClient.cancelQueries(hcoKey);
    queryClient.setQueryData<HealthCareOrganization>(hcoKey, old => ({ ...old!, status: newStatus }));
    try {
      await mutateAsync({ ...hco, id, status: newStatus });
    } catch (err) {
      queryClient.setQueryData(hcoKey, rollbackData);
      setErrors(err);
    }
  };

  const mainEm = hco && (
    <>
      <Pane mb={2}>
        <HFlex alignItems="center">
          <Text paragraph mb={0}>
            <Icon of={Circle} success={hco.status === "active"} size={0.7} />
            <Text ml={1} bold>
              {hco.id}
            </Text>
          </Text>
          <Text heading bold nowrap mb={0} style={{ flex: 1, textAlign: "center" }}>
            {hco.name}
          </Text>
          <HFlex component="label" alignItems="center" style={{ flexShrink: 0, cursor: hco.platform_fee_model === "percentage" ? "pointer" : "default" }}>
            <Text small mb={0} secondary={hco.platform_fee_model !== "percentage"}>
              {hco.status === "active" ? "Active" : "Inactive"}
            </Text>
            <Switch
              color="primary"
              checked={hco.status === "active"}
              onChange={handleStatusToggle}
              disabled={hco.platform_fee_model !== "percentage"}
            />
          </HFlex>
        </HFlex>
      </Pane>
      <Text paragraph caption secondary>
        {t("form.hco_details_short")}
      </Text>
      <Pane>
        <HcoInfo hco={hco} />
      </Pane>
    </>
  );

  const altEm = (
    <Pane>
      {error && <ErrorBox error={error} verbose dark />}
      {isLoading && <Loading />}
    </Pane>
  );

  return (
    <div>
      <Text paragraph caption secondary>
        {t("form.hco_details")}
      </Text>
      {isSuccess ? mainEm : altEm}
    </div>
  );
};